/**
 * readTelemetry — counts Firestore document reads per company per SCREEN.
 *
 * Answers "where are the millions of reads going?" from inside the app —
 * something the Firebase console can never attribute. Every instrumented
 * data-helper calls recordReads(companyId, n) after a fetch; the count is
 * attributed to the current route (the screen that triggered it), batched
 * in memory, and flushed at most once per minute per company to:
 *
 *   companies/{companyId}/readStats/{YYYY-MM-DD}
 *     { total: <n>, routes: { <route>: <n>, ... }, updatedAt }
 *
 * The Super Admin "Reads" tab renders these docs. Telemetry cost: one small
 * WRITE per active user per minute at most (writes are ~3% of read price),
 * and it must NEVER break or slow the app — every path is try/caught.
 *
 * Coverage note: instrumented at the helper choke points that carry the bulk
 * of read volume (transaction lists/pages, sub-transactions, stock flow,
 * dashboard fetches, POS master sync, balances). Reports that query Firestore
 * directly are attributed when they use those helpers; a few direct queries
 * are not counted — the Super Admin view is a map of where reads go, not an
 * exact invoice (the Firebase console remains the billing truth).
 */

import { doc, setDoc, increment } from 'firebase/firestore';
import { db } from '../config/firebase';

const FLUSH_MS = 60 * 1000;

const pending = new Map(); // companyId -> { total, routes: { route: n } }
let flushTimer = null;

const routeKey = () => {
  try {
    const path = (window.location.pathname || '/').replace(/^\/+|\/+$/g, '');
    if (!path) return 'dashboard';
    // Field-name safe: no slashes/dots, bounded length.
    return path.replace(/[.~*/[\]]/g, '_').replace(/\//g, '__').slice(0, 80);
  } catch {
    return 'unknown';
  }
};

const flush = () => {
  flushTimer = null;
  try {
    const today = new Date().toISOString().split('T')[0];
    for (const [companyId, entry] of pending) {
      const routes = {};
      for (const [r, n] of Object.entries(entry.routes)) routes[r] = increment(n);
      setDoc(doc(db, 'companies', companyId, 'readStats', today), {
        total: increment(entry.total),
        routes,
        updatedAt: new Date().toISOString()
      }, { merge: true }).catch(() => { /* telemetry loss is acceptable */ });
    }
    pending.clear();
  } catch { /* never break the app */ }
};

/**
 * Record `count` Firestore document reads for `companyId`, attributed to the
 * screen currently on route (or an explicit `tag`).
 */
export const recordReads = (companyId, count, tag = null) => {
  try {
    const n = Number(count);
    if (!companyId || !Number.isFinite(n) || n <= 0) return;
    const key = tag || routeKey();
    let entry = pending.get(companyId);
    if (!entry) { entry = { total: 0, routes: {} }; pending.set(companyId, entry); }
    entry.total += n;
    entry.routes[key] = (entry.routes[key] || 0) + n;
    if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_MS);
  } catch { /* never break the app */ }
};

// Last-chance flush when the tab is hidden/closed so counts aren't lost.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
